import { useRef } from 'react';
import { useNearViewport } from '@/hooks/useNearViewport';
// Type-only: erased at build time, so it does not pull Leaflet into this chunk.
import type { LeafletMapProps } from './LeafletMap';
import { ClientLeafletMap, MapPlaceholder } from './ClientLeafletMap';

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/**
 * LeafletMap that costs nothing until it is about to scroll into view.
 *
 * Leaflet, react-leaflet and their CSS are ~45 KB gzipped, and the map card on
 * a shop/store page sits below the fold in the aside. Imported statically, all
 * of it downloaded and parsed with the page chunk, competing with the page's
 * own data and LCP image. Here the chunk is requested only once the slot comes
 * within 200 px of the viewport, and the slot keeps its size meanwhile (the
 * caller sizes it through `className`), so nothing shifts when the map lands.
 * Browser-only like `ClientLeafletMap` (web plan W4.2): the server and the
 * hydration render show the placeholder.
 */
export function LazyLeafletMap({ className, ...props }: LeafletMapProps) {
  const slotRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(slotRef);

  return (
    <div ref={slotRef} className={cn('relative', className)}>
      {near ? <ClientLeafletMap {...props} className="h-full" /> : <MapPlaceholder />}
    </div>
  );
}

export default LazyLeafletMap;
